import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { EmployeeService } from '../../services/employee.service';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { ApiResponse } from '../../../shared/types';
import {
  EmployeeReward,
  RewardSettings,
  UpdateRewardSettingsRequest,
} from '../../models/employee.models';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { SearchbarComponent } from '../../../shared/components/searchbar/searchbar.component';
import { AddButtonComponent } from '../../../shared/components/add-button/add-button.component';
import { RewardSettingsModalComponent } from '../../../components/reward-settings-modal/reward-settings-modal.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption as DateRangePeriodOption,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import { TableUtils, DragScrollConfig } from '../../utils/table.utils';
import { getDateRangeForPeriod } from '../../../rewards/utils/rewards-period.util';
import {
  formatCalendarDateInTimeZone,
  formatDateToISO,
  getUserTimezone,
} from '../../../shared/utils/date.util';

/**
 * Displays an employee rewards dashboard tab with searchable, paginated reward statistics and reward-settings management.
 * @component
 * @selector app-rewards-tab-v1
 * @example
 *   <app-rewards-tab></app-rewards-tab>
 * @input none - This component does not declare any @Input properties.
 * @output none - This component does not declare any @Output events.
 * @description
 * Provides a rewards summary table that can be filtered by date range and search term, supports pagination,
 * opens/closes a reward date-range picker, and loads/updates reward settings through a modal dialog.
 * Key methods include load() for refreshing tab data, onRewardsSearchChange() for debounced search,
 * onRewardDateRangeApply() for applying date filters, onSaveRewardSettings() for persisting settings,
 * and helper methods for pagination, formatting, and employee initials.
 * Lifecycle hooks:
 * - ngOnInit() sets up debounced reward search handling.
 * - ngAfterViewInit() initializes drag-scrolling for the rewards table.
 * - ngOnDestroy() cleans up subscriptions and table utilities.
 */
@Component({
  selector: 'app-rewards-tab-v1',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    SearchbarComponent,
    AddButtonComponent,
    RewardSettingsModalComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './rewards-tab-v1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-tab-v1.component.scss'],
})
export class RewardsTabV1Component implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('rewardsTableScrollWrapper') rewardsTableScrollWrapper!: ElementRef;
  @ViewChild(RewardSettingsModalComponent) rewardSettingsModal!: RewardSettingsModalComponent;

  private destroy$ = new Subject<void>();
  private rewardsDragScrollConfig!: DragScrollConfig;
  private rewardsSearchSubject = new Subject<string>();

  rewards: EmployeeReward[] = [];
  filteredRewards: EmployeeReward[] = [];
  paginatedRewards: EmployeeReward[] = [];
  rewardsSearchTerm = '';
  selectedRewardPeriod = 'Today';
  loadingRewards = false;
  isRewardDateRangePickerOpen = false;
  rewardCustomDateRange: DateRange = { startDate: null, endDate: null };

  showRewardSettingsModal = false;
  rewardSettings: RewardSettings | null = null;
  isLoadingRewardSettings = false;

  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 1;

  rewardPeriodOptions: DateRangePeriodOption[] = [
    { label: 'Today', value: 'Today' },
    { label: 'This Week', value: 'This Week' },
    { label: 'This Month', value: 'This Month' },
    { label: 'Last 6 Months', value: 'Last 6 Months' },
    { label: 'This Year', value: 'This Year' },
    { label: 'Custom', value: 'Custom' },
  ];

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Employee', width: 'md' },
    { label: 'Total Rewards Earned', width: 'md' },
    { label: 'Membership Rewards', width: 'md' },
    { label: 'Threshold Rewards', width: 'md' },
  ];

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  constructor(
    private employeeService: EmployeeService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.rewardsSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => this.loadRewardsObservable(term)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: ApiResponse<EmployeeReward[]>) => this.applyRewardsResponse(response),
        error: () => this.handleRewardsError(),
      });
    // Load when tab mounts (*ngIf); do not rely only on parent ViewChild + setTimeout.
    this.load();
  }

  ngAfterViewInit(): void {
    this.reinitDragScroll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    TableUtils.cleanup();
  }

  /** Called by parent when this tab becomes active. */
  load(): void {
    this.loadingRewards = true;
    this.loadRewards();
    this.loadRewardSettings();
  }

  /**
   * Closes the reward date range picker when the user clicks outside the dropdown wrapper and calendar panels.
   * @example
   * onDocumentClick(event)
   * void
   * @param {Event} event - The click event triggered on the document.
   * @returns {void} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdownWrapper = target.closest('.period-dropdown-wrapper');
    const allCalendarPanels = document.querySelectorAll('.p-datepicker-panel');
    let isInsideCalendarPanel = false;
    allCalendarPanels.forEach(panel => {
      if (panel.contains(target)) isInsideCalendarPanel = true;
    });

    if (!dropdownWrapper && !isInsideCalendarPanel) {
      this.isRewardDateRangePickerOpen = false;
    }
  }

  private loadRewards(searchTerm?: string): void {
    this.loadRewardsObservable(searchTerm).subscribe({
      next: response => this.applyRewardsResponse(response),
      error: () => this.handleRewardsError(),
    });
  }

  private loadRewardsObservable(searchTerm?: string) {
    const { startDate, endDate } = this.getRewardDateRange();
    return this.employeeService
      .getRewardStatistics(startDate, endDate, searchTerm)
      .pipe(takeUntil(this.destroy$));
  }

  /**
   * Applies the rewards API response to the component state, normalizing employee IDs and updating UI flags.
   * @example
   * applyRewardsResponse(response)
   * void
   * @param {ApiResponse<EmployeeReward[]>} response - API response containing an array of employee rewards.
   * @returns {void} No return value.
   **/
  /**
   * API may return `data: EmployeeReward[]` or `data: { rewards: EmployeeReward[], pagination }` (see RewardApiResponse).
   */
  private extractRewardsList(data: any): EmployeeReward[] {
    if (data == null) {
      return [];
    }
    if (Array.isArray(data)) {
      return data as EmployeeReward[];
    }
    if (
      typeof data === 'object' &&
      'rewards' in data &&
      Array.isArray((data as { rewards: any }).rewards)
    ) {
      return (data as { rewards: EmployeeReward[] }).rewards;
    }
    if (typeof data === 'object' && 'data' in data && Array.isArray((data as { data: any }).data)) {
      return (data as { data: EmployeeReward[] }).data;
    }
    return [];
  }

  private applyRewardsResponse(response: ApiResponse<EmployeeReward[]>): void {
    try {
      const list = this.extractRewardsList(response?.data as any);
      const statusCode = (response as { status_code?: number } | undefined)?.status_code;
      const ok = response?.status === true || statusCode === 200;
      if (ok) {
        this.rewards = list.map(r => ({
          ...r,
          emp_id: (r as { emp_id?: string }).emp_id || r.employee_id,
        }));
        this.filteredRewards = [...this.rewards];
      } else {
        this.rewards = [];
        this.filteredRewards = [];
      }
    } finally {
      this.loadingRewards = false;
      this.updatePagination();
      this.reinitDragScroll();
    }
  }

  private handleRewardsError(): void {
    this.rewards = [];
    this.filteredRewards = [];
    this.loadingRewards = false;
    this.updatePagination();
    this.reinitDragScroll();
  }

  onRewardsSearchChange(searchTerm?: string): void {
    const term = searchTerm !== undefined ? searchTerm : this.rewardsSearchTerm;
    this.rewardsSearchSubject.next(term);
  }

  toggleRewardDateRangePicker(): void {
    this.isRewardDateRangePickerOpen = !this.isRewardDateRangePickerOpen;
  }

  closeRewardDateRangePicker(): void {
    this.isRewardDateRangePickerOpen = false;
  }

  onRewardDateRangePeriodChange(period: string): void {
    this.selectedRewardPeriod = period;
  }

  onRewardCustomDateRangeChange(dateRange: DateRange): void {
    this.rewardCustomDateRange = dateRange;
  }

  onRewardDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedRewardPeriod = data.period;
    if (data.customRange) this.rewardCustomDateRange = data.customRange;
    this.loadingRewards = true;
    this.loadRewards();
    this.closeRewardDateRangePicker();
  }

  onRewardSettings(): void {
    this.loadRewardSettings();
    this.showRewardSettingsModal = true;
  }

  onCloseRewardSettingsModal(): void {
    this.showRewardSettingsModal = false;
  }

  /**
   * Saves the reward settings by sending the provided settings to the employee service and updates the UI state on success or failure.
   * @example
   * nSaveRewardSettings(settings)
   * void
   * @param {UpdateRewardSettingsRequest} settings - The reward settings payload to be updated.
   * @returns {void} Does not return a value.
   **/
  onSaveRewardSettings(settings: UpdateRewardSettingsRequest): void {
    this.isLoadingRewardSettings = true;
    this.employeeService
      .updateRewardSettings(settings)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => {
          this.rewardSettings = updated;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Reward settings updated successfully',
          });
          this.showRewardSettingsModal = false;
          this.isLoadingRewardSettings = false;
          this.rewardSettingsModal?.resetSubmittingState();
          this.loadRewardSettings();
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update reward settings',
          });
          this.isLoadingRewardSettings = false;
          this.rewardSettingsModal?.resetSubmittingState();
        },
      });
  }

  /**
   * Loads the current reward settings from the employee service and updates the component state.
   * @example
   * oadRewardSettings()
   * void
   * @returns {void} No return value.
   */
  loadRewardSettings(): void {
    if (!this.isLoadingRewardSettings) this.isLoadingRewardSettings = true;
    this.employeeService
      .getRewardSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: settings => {
          this.rewardSettings = settings;
          this.isLoadingRewardSettings = false;
        },
        error: error => {
          if (this.showRewardSettingsModal) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.message || 'Failed to load reward settings',
            });
          }
          this.isLoadingRewardSettings = false;
        },
      });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 1;
    this.updatePagination();
  }

  formatCurrency(amount: number | string): string {
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${n.toFixed(2)}`;
  }

  getEmployeeInitialsForReward(name: string): string {
    const n = name?.trim();
    if (!n) return 'NA';
    const parts = n.split(/\s+/).filter(Boolean);
    if (!parts.length) return 'NA';
    const first = parts[0]?.charAt(0) ?? '';
    const second =
      parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : (parts[0]?.charAt(1) ?? '');
    const initials = `${first}${second}`.trim();
    return initials ? initials.toUpperCase() : n.substring(0, 2).toUpperCase();
  }

  trackByRewardId(_: number, reward: EmployeeReward): string {
    return reward.employee_id;
  }

  private updatePagination(): void {
    this.totalItems = this.filteredRewards.length;
    this.totalPages =
      this.totalItems > 0 ? Math.max(Math.ceil(this.totalItems / this.itemsPerPage), 1) : 1;
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedRewards = this.filteredRewards.slice(start, start + this.itemsPerPage);
  }

  /**
   * Gets the reward date range for the selected reward period.
   * @example
   * getRewardDateRange()
   * { startDate: '2024-01-01', endDate: '2024-01-31' }
   * @returns {{ startDate: string; endDate: string }} The computed reward date range with ISO-formatted start and end dates.
   **/
  private getRewardDateRange(): { startDate: string; endDate: string } {
    const range = getDateRangeForPeriod(this.selectedRewardPeriod, this.rewardCustomDateRange);
    if (range) {
      return { startDate: range.start, endDate: range.end };
    }

    // Fallback: shop-timezone "today" (avoids browser-local off-by-one).
    const today = new Date();
    const endDate =
      formatCalendarDateInTimeZone(today, getUserTimezone()) || formatDateToISO(today);
    return { startDate: endDate, endDate };
  }

  private reinitDragScroll(): void {
    setTimeout(() => {
      if (this.rewardsTableScrollWrapper) {
        this.rewardsDragScrollConfig = TableUtils.createDragScrollConfig(
          this.rewardsTableScrollWrapper
        );
        TableUtils.initializeDragScrolling(this.rewardsDragScrollConfig);
      }
    }, 100);
  }
}
